import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { 
  FaArrowLeft, FaBox, FaMapMarkerAlt, FaPhone, FaEnvelope, 
  FaShippingFast, FaCheckCircle, FaClock, FaTimes, FaFileInvoice 
} from 'react-icons/fa';
import './OrderDetail.css';

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      const response = await api.get(`/orders/${id}`);
      setOrder(response.data.order);
    } catch (error) {
      console.error('Failed to fetch order:', error);
      toast.error(error.response?.data?.message || 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      await api.put(`/orders/${id}/status`, { status: newStatus });
      toast.success('Order status updated successfully');
      fetchOrder();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update order status');
    }
  };

  const handleCancelOrder = async () => {
    if (window.confirm('Are you sure you want to cancel this order?')) {
      setCancelling(true);
      try {
        await api.put(`/orders/${id}/cancel`);
        toast.success('Order cancelled successfully');
        fetchOrder();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to cancel order');
      } finally {
        setCancelling(false);
      }
    }
  };

  const handleCompletePayment = async () => {
    if (window.confirm('Complete payment for this order? (Demo mode - no actual payment required)')) {
      try {
        const response = await api.post('/payments/complete', {
          orderId: id,
          paymentMethod: order.payment.method
        });
        toast.success('Payment completed successfully! Invoice generated.');
        
        // Navigate to invoice if generated
        if (response.data.invoiceId) {
          setTimeout(() => {
            navigate(`/invoices/${response.data.invoiceId}`);
          }, 1500);
        } else {
          fetchOrder();
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to complete payment');
      }
    }
  };

  const handleViewInvoice = () => {
    if (order.invoiceId) {
      navigate(`/invoices/${order.invoiceId}`);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      interest: '#9ca3af',
      pending_payment: '#f59e0b',
      paid: '#3b82f6',
      confirmed: '#10b981',
      processing: '#8b5cf6',
      shipped: '#06b6d4',
      in_transit: '#0ea5e9',
      delivered: '#22c55e',
      completed: '#059669',
      cancelled: '#ef4444',
      refunded: '#f97316'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'delivered':
        return <FaCheckCircle />;
      case 'cancelled':
        return <FaTimes />;
      case 'shipped':
      case 'in_transit':
        return <FaShippingFast />;
      default:
        return <FaClock />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading order details...</div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div className="error-message">
          <h2>Order not found</h2>
          <button onClick={() => navigate('/orders')} className="btn-primary">
            Back to Orders
          </button>
        </div>
      </Layout>
    );
  }

  const isBuyer = user?._id === order.buyer._id;
  const isSeller = user?._id === order.seller._id;

  return (
    <Layout>
      <div className="order-detail">
        {/* Header */}
        <div className="order-detail-header">
          <div className="header-left">
            <button className="btn-back" onClick={() => navigate('/orders')}>
              <FaArrowLeft /> Back to Orders
            </button>
            <div className="order-number">
              <h1>Order #{order.orderNumber}</h1>
              <span 
                className="status-badge" 
                style={{ backgroundColor: getStatusColor(order.status) }}
              >
                {getStatusIcon(order.status)}
                {order.status.replace(/_/g, ' ').toUpperCase()}
              </span>
            </div>
          </div>
          
          <div className="header-actions">
            {isBuyer && order.status === 'pending_payment' && (
              <button className="btn-primary btn-pay" onClick={handleCompletePayment}>
                <FaCheckCircle /> Complete Payment
              </button>
            )}
            {isBuyer && ['pending_payment', 'confirmed', 'processing'].includes(order.status) && (
              <button className="btn-cancel" onClick={handleCancelOrder} disabled={cancelling}>
                <FaTimes /> {cancelling ? 'Cancelling...' : 'Cancel Order'}
              </button>
            )}
            {order.invoiceId && (
              <button className="btn-invoice-link" onClick={handleViewInvoice}>
                <FaFileInvoice /> View Invoice
              </button>
            )}
          </div>
        </div>

        <div className="order-detail-content">
          {/* Left Column - Order Items & Timeline */}
          <div className="order-main">
            {/* Order Items */}
            <div className="section-card">
              <h2>Order Items</h2>
              <div className="order-items">
                {order.items.map((item, index) => (
                  <div key={index} className="order-item">
                    <div className="item-image">
                      {item.product?.images?.[0]?.url ? (
                        <img src={item.product.images[0].url} alt={item.product.title} />
                      ) : (
                        <div className="no-image">
                          <FaBox />
                        </div>
                      )}
                    </div>
                    <div className="item-details">
                      <h3>{item.product?.title || 'Product'}</h3>
                      <p className="item-price">₹{item.price.toFixed(2)} × {item.quantity}</p>
                      <p className="item-gst">GST: {item.gst}%</p>
                    </div>
                    <div className="item-total">
                      <p className="total-label">Total</p>
                      <p className="total-amount">₹{(item.price * item.quantity * (1 + item.gst / 100)).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Order Timeline */}
            <div className="section-card">
              <h2>Order Timeline</h2>
              <div className="order-timeline">
                {order.milestones?.map((milestone, index) => (
                  <div key={index} className="timeline-item">
                    <div className="timeline-dot" style={{ backgroundColor: getStatusColor(milestone.status) }}></div>
                    <div className="timeline-content">
                      <p className="timeline-status">{milestone.status.replace(/_/g, ' ').toUpperCase()}</p>
                      <p className="timeline-date">{new Date(milestone.timestamp).toLocaleString()}</p>
                      {milestone.note && <p className="timeline-note">{milestone.note}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Shipping Information */}
            {order.shipping?.address && (
              <div className="section-card">
                <h2>Shipping Information</h2>
                <div className="shipping-info">
                  <div className="shipping-address">
                    <h4>Delivery Address</h4>
                    <p>{order.shipping.address.name}</p>
                    <p>{order.shipping.address.phone}</p>
                    <p>{order.shipping.address.street}</p>
                    <p>{order.shipping.address.city}, {order.shipping.address.state} - {order.shipping.address.pincode}</p>
                  </div>
                  {order.shipping.trackingId && (
                    <div className="tracking-info">
                      <h4>Tracking Details</h4>
                      <p><strong>Courier:</strong> {order.shipping.courier}</p>
                      <p><strong>Tracking ID:</strong> {order.shipping.trackingId}</p>
                      {order.shipping.estimatedDelivery && (
                        <p><strong>Est. Delivery:</strong> {new Date(order.shipping.estimatedDelivery).toLocaleDateString()}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Summary & Actions */}
          <div className="order-sidebar">
            {/* Price Summary */}
            <div className="section-card">
              <h2>Price Summary</h2>
              <div className="price-breakdown">
                <div className="price-row">
                  <span>Subtotal</span>
                  <span>₹{order.pricing.subtotal.toFixed(2)}</span>
                </div>
                <div className="price-row">
                  <span>GST</span>
                  <span>₹{order.pricing.gst.toFixed(2)}</span>
                </div>
                <div className="price-row">
                  <span>Platform Fee</span>
                  <span>₹{order.pricing.platformFee.toFixed(2)}</span>
                </div>
                <div className="price-row total">
                  <span>Total Amount</span>
                  <span>₹{order.pricing.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Payment Information */}
            <div className="section-card">
              <h2>Payment Details</h2>
              <div className="payment-info">
                <div className="info-row">
                  <span>Method</span>
                  <span className="method-badge">{order.payment.method.toUpperCase()}</span>
                </div>
                <div className="info-row">
                  <span>Status</span>
                  <span 
                    className="payment-status"
                    style={{ color: order.payment.status === 'completed' ? '#10b981' : '#f59e0b' }}
                  >
                    {order.payment.status.toUpperCase()}
                  </span>
                </div>
                {order.payment.transactionId && (
                  <div className="info-row">
                    <span>Transaction ID</span>
                    <span className="transaction-id">{order.payment.transactionId}</span>
                  </div>
                )}
                {order.payment.paidAt && (
                  <div className="info-row">
                    <span>Paid At</span>
                    <span>{new Date(order.payment.paidAt).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Seller/Buyer Information */}
            <div className="section-card">
              <h2>{isBuyer ? 'Seller' : 'Buyer'} Information</h2>
              <div className="party-info">
                {isBuyer ? (
                  <>
                    <p><strong>{order.seller.businessName}</strong></p>
                    <p className="contact-item"><FaPhone /> {order.seller.phone}</p>
                    <p className="contact-item"><FaEnvelope /> {order.seller.email}</p>
                    {order.seller.address && (
                      <p className="contact-item">
                        <FaMapMarkerAlt /> {order.seller.address.city}, {order.seller.address.state}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p><strong>{order.buyer.businessName}</strong></p>
                    <p className="contact-item"><FaPhone /> {order.buyer.phone}</p>
                    <p className="contact-item"><FaEnvelope /> {order.buyer.email}</p>
                    {order.buyer.address && (
                      <p className="contact-item">
                        <FaMapMarkerAlt /> {order.buyer.address.city}, {order.buyer.address.state}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Seller Actions */}
            {isSeller && (
              <div className="section-card">
                <h2>Order Actions</h2>
                <div className="action-buttons">
                  {order.status === 'paid' && (
                    <button 
                      className="btn-action btn-confirm"
                      onClick={() => handleUpdateStatus('confirmed')}
                    >
                      <FaCheckCircle /> Confirm Order
                    </button>
                  )}
                  {order.status === 'confirmed' && (
                    <button 
                      className="btn-action btn-process"
                      onClick={() => handleUpdateStatus('processing')}
                    >
                      <FaClock /> Start Processing
                    </button>
                  )}
                  {order.status === 'processing' && (
                    <button 
                      className="btn-action btn-ship"
                      onClick={() => handleUpdateStatus('shipped')}
                    >
                      <FaShippingFast /> Mark as Shipped
                    </button>
                  )}
                  {order.status === 'shipped' && (
                    <button 
                      className="btn-action btn-deliver"
                      onClick={() => handleUpdateStatus('delivered')}
                    >
                      <FaCheckCircle /> Mark as Delivered
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Invoice */}
            {order.invoiceId && (
              <div className="section-card">
                <h2>Invoice</h2>
                <button 
                  onClick={handleViewInvoice}
                  className="btn-invoice"
                >
                  <FaFileInvoice /> View Invoice
                </button>
                <a 
                  href={`/api/invoices/${order.invoiceId}/download`}
                  download
                  className="btn-invoice btn-download"
                  style={{ marginTop: '10px' }}
                >
                  <FaFileInvoice /> Download PDF
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default OrderDetail;
